/**
 * Admin → Algemene mailboxen → ikwilwerken@ (recht werving; ook zonder admin_settings).
 * Concepten aan/uit (ALGMAIL_WERVING, env = plafond), sjablonen (herbruikbare WervingSjablonen) en
 * routering per categorie (alleen interne ontvangers).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { useUserRole } from '../../../contexts/UserRoleContext';
import { bevestig } from '../../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../../utils/ongedaan';
import WervingSjablonen from '../../../ui-components/WervingSjablonen';
import Schakelaar from './Schakelaar';
import { adressen, mailboxApi, wervingApi } from './api';
import { wervingApi as atsApi } from '../../Werving/wervingApi';

const GRIJS = '#556071';
const RAND = '#e5e9eb';

const Kop = ({ children, sub }) => (
  <Box sx={{ mt: 5, mb: 1.5 }}>
    <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{children}</Typography>
    {sub && <Typography sx={{ fontSize: '0.8rem', color: GRIJS }}>{sub}</Typography>}
  </Box>
);

function Routering({ api, categorieen, labels, kanBewerken }) {
  const [routes, setRoutes] = useState(null);
  const [vorm, setVorm] = useState({});
  const laad = useCallback(async () => {
    try {
      const d = await api.routering();
      setRoutes(d.routering || []);
      setVorm(Object.fromEntries((d.routering || []).map((r) => [r.categorie, r.ontvangers.join(', ')])));
    } catch (e) { toast.error(e.message); setRoutes([]); }
  }, [api]);
  useEffect(() => { laad(); }, [laad]);

  const opslaan = async (cat) => {
    const lijst = adressen(vorm[cat]);
    try {
      if (!lijst.length && cat !== '*') {
        if (routes.some((r) => r.categorie === cat)) {
          const ok = await bevestig({ titel: 'Eigen ontvangers weghalen?', tekst: `${labels[cat] || cat} valt dan terug op de standaard.`, knop: 'Weghalen' });
          if (!ok) return;
          await api.verwijderRoute(cat);
        }
      } else {
        await api.zetRoute(cat, lijst);
      }
      toast.success('Opgeslagen');
      await laad();
    } catch (e) { toast.error(e.message); }
  };

  if (!routes) return <CircularProgress size={18} sx={{ color: '#adb3b5' }} />;
  return (
    <Box>
      {['*', ...categorieen].map((cat) => (
        <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderTop: `1px solid ${RAND}`, flexWrap: 'wrap' }}>
          <Typography sx={{ width: 200, fontSize: '0.88rem' }}>{cat === '*' ? 'Standaard' : (labels[cat] || cat)}</Typography>
          <TextField size="small" sx={{ flex: 1, minWidth: 240 }} disabled={!kanBewerken}
            placeholder={cat === '*' ? 'naam@fullframe.nl' : 'leeg = standaard'}
            value={vorm[cat] || ''} onChange={(e) => setVorm({ ...vorm, [cat]: e.target.value })} />
          {kanBewerken && (
            <Button size="small" onClick={() => opslaan(cat)} sx={{ textTransform: 'none' }}>Opslaan</Button>
          )}
        </Box>
      ))}
    </Box>
  );
}

const STAND = {
  off: 'Uit — er gaat niets uit. Joris zet hem aan met WERVING_HERINNERING=dry (proef) of on.',
  dry: 'Proefstand — elke maandag een regel in de log, er gaat nog geen mail uit.',
  on: 'Aan — elke maandag om 08:00 een interne mail.',
};

/** Wekelijkse herinnering (maandag 08:00): wie nog geen reactie heeft gehad. Ontvangers = route 'herinnering'. */
function Herinnering({ api, herinnering, kanBewerken, onGewijzigd }) {
  const route = herinnering?.route || 'herinnering';
  const [waarde, setWaarde] = useState('');
  const [voorbeeld, setVoorbeeld] = useState(null);
  const [eigen, setEigen] = useState(null);
  const laadEigen = useCallback(async () => {
    try {
      const d = await api.routering();
      const r = (d.routering || []).find((x) => x.categorie === route);
      setEigen(r ? r.ontvangers : []);
      setWaarde(r ? r.ontvangers.join(', ') : '');
    } catch (e) { toast.error(e.message); setEigen([]); }
  }, [api, route]);
  useEffect(() => { laadEigen(); }, [laadEigen]);

  const opslaan = async () => {
    const lijst = adressen(waarde);
    const vorige = eigen || [];
    try {
      if (!lijst.length) {
        if (!vorige.length) return;
        await api.verwijderRoute(route);
      } else {
        await api.zetRoute(route, lijst);
      }
      biedOngedaanAan('Ontvangers herinnering bijgewerkt', async () => {
        if (vorige.length) await api.zetRoute(route, vorige); else await api.verwijderRoute(route);
        await laadEigen(); onGewijzigd();
      });
      await laadEigen(); onGewijzigd();
    } catch (e) { toast.error(e.message); }
  };

  const bekijk = async () => {
    try { setVoorbeeld(await atsApi.herinnering()); } catch (e) { toast.error(e.message); }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: '0.84rem', color: GRIJS, mb: 1 }}>
        {STAND[herinnering?.schakelaar] || STAND.off} Wie er nu in staat: {herinnering?.ontvangers?.join(', ') || '—'}.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderTop: `1px solid ${RAND}`, flexWrap: 'wrap' }}>
        <Typography sx={{ width: 200, fontSize: '0.88rem' }}>Ontvangers</Typography>
        <TextField size="small" sx={{ flex: 1, minWidth: 240 }} disabled={!kanBewerken} placeholder="leeg = de standaardroute"
          value={waarde} onChange={(e) => setWaarde(e.target.value)} inputProps={{ 'aria-label': 'Ontvangers herinnering' }} />
        {kanBewerken && <Button size="small" onClick={opslaan} sx={{ textTransform: 'none' }}>Opslaan</Button>}
      </Box>
      {kanBewerken && <Button size="small" onClick={bekijk} sx={{ textTransform: 'none', px: 0, mt: 1 }}>Wie staat er deze week in?</Button>}
      {voorbeeld && (
        <Box sx={{ mt: 1, p: 2, bgcolor: '#f8f9fa', borderRadius: 1.5 }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 600 }}>
            {voorbeeld.aantal} sollicitant{voorbeeld.aantal === 1 ? '' : 'en'} zonder reactie (langst wachtend eerst)
          </Typography>
          {(voorbeeld.sollicitaties || []).slice(0, 10).map((x) => (
            <Typography key={x.id} sx={{ fontSize: '0.8rem', color: GRIJS }}>
              {x.naam} · {x.waar} · {x.fase_label}{x.wacht_dagen != null ? ` · ${x.wacht_dagen} d` : ''}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function IkwilwerkenMailbox() {
  const { permissions } = useUserRole();
  const kanBewerken = !!permissions?.werving?.edit;
  const [st, setSt] = useState(null);
  const api = useMemo(() => wervingApi(), []);

  useEffect(() => registreerOngedaanSneltoets(), []);
  const laad = useCallback(async () => {
    try { setSt(await mailboxApi.werving()); } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  if (!st) return <CircularProgress size={20} sx={{ color: '#adb3b5' }} />;
  const labels = st.categorie_labels || {};

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <Typography sx={{ fontSize: '0.84rem', color: GRIJS }}>
        {st.uitleg} De tool stuurt nooit zelf iets naar een sollicitant.
      </Typography>

      <Kop>Concepten</Kop>
      <Box sx={{ borderTop: `1px solid ${RAND}` }}>
        <Schakelaar rij={st.schakelaar} titel="Concepten in ikwilwerken@" kanBewerken={kanBewerken}
          onZet={async (niveau) => { await mailboxApi.zetConcepten(niveau); await laad(); }} />
      </Box>

      <Box sx={{ mt: 5 }}>
        <WervingSjablonen api={api} kanBewerken={kanBewerken} categorieLabels={labels} />
      </Box>

      <Kop sub="Wie de interne mail per sollicitatie krijgt. Alleen interne adressen.">Routering</Kop>
      <Routering api={api} categorieen={st.categorieen || []} labels={labels} kanBewerken={kanBewerken} />

      <Kop sub={`Elke ${st.herinnering?.moment || 'maandag 08:00'} een interne mail met wie nog geen reactie heeft gehad. Alleen interne adressen.`}>
        Wekelijkse herinnering
      </Kop>
      <Herinnering api={api} herinnering={st.herinnering} kanBewerken={kanBewerken} onGewijzigd={laad} />
    </Box>
  );
}
